// Compilateur — partie TABLEAUX (lots F4 / F5) : pièges, messages, touches et coups de pouce des tableaux.
// Le noyau (src/kernel/tables.js) fournit le catalogue fermé des sortes de tableaux et de leurs détecteurs ;
// les packs choisissent les détecteurs actifs et les rédigent.
//
//   engine.tables = { kinds: { [sorte]: { label, hint, hints: { [case]: gabarit }, traps: { [détecteur]: { title, why } },
//                                       wrong: { title, value, sign, arrow }, unfinished: { title, why } } },
//                     keys: { sign: [...], arrow: [...], bound: [...] }, words: { [sorte]: [...] }, pack }

import { TABLE_KINDS, CHOICE_ROLES } from '../kernel/tables.js';

const KIND_FIELDS = ['label', 'hint', 'hints', 'traps', 'wrong', 'unfinished', 'notes'];
const isStr = (s) => typeof s === 'string' && s.trim().length > 0;

export function buildTables(packs) {
  const diagnostics = [];
  const err = (pack, msg) => diagnostics.push({ level: 'error', pack, msg });
  const warn = (pack, msg) => diagnostics.push({ level: 'warning', pack, msg });
  let out = null;
  for (const pack of packs) {
    const t = pack.tables;
    if (t == null) continue;
    if (typeof t !== 'object' || Array.isArray(t)) {
      err(pack.id, '« tables » doit être un objet { kinds, keys }');
      continue;
    }
    out ||= { kinds: {}, keys: {}, pack: pack.id };
    out.pack = pack.id;
    for (const [kind, K] of Object.entries(t.kinds || {})) {
      if (!TABLE_KINDS[kind]) {
        err(
          pack.id,
          `tables.kinds : sorte « ${kind} » inconnue (${Object.keys(TABLE_KINDS).join(', ')})`,
        );
        continue;
      }
      const dst = (out.kinds[kind] ||= { traps: {}, hints: {}, notes: {} });
      for (const f of Object.keys(K))
        if (!KIND_FIELDS.includes(f)) warn(pack.id, `tables.kinds.${kind} : champ « ${f} » ignoré`);
      for (const f of ['label', 'hint'])
        if (K[f] != null) {
          if (isStr(K[f])) dst[f] = K[f];
          else err(pack.id, `tables.kinds.${kind}.${f} doit être un texte`);
        }
      for (const [k, v] of Object.entries(K.hints || {})) {
        if (isStr(v)) dst.hints[k] = v;
        else err(pack.id, `tables.kinds.${kind}.hints.${k} doit être un texte`);
      }
      for (const [k, v] of Object.entries(K.notes || {})) {
        if (isStr(v)) dst.notes[k] = v;
        else err(pack.id, `tables.kinds.${kind}.notes.${k} doit être un texte`);
      }
      for (const [det, tr] of Object.entries(K.traps || {})) {
        if (!TABLE_KINDS[kind].includes(det)) {
          err(
            pack.id,
            `tables.kinds.${kind}.traps : détecteur « ${det} » inconnu (${TABLE_KINDS[kind].join(', ')})`,
          );
          continue;
        }
        if (!isStr(tr?.title) || !isStr(tr?.why)) {
          err(
            pack.id,
            `tables.kinds.${kind}.traps.${det} : « title » et « why » sont obligatoires`,
          );
          continue;
        }
        dst.traps[det] = { title: tr.title, why: tr.why };
      }
      if (K.wrong != null) dst.wrong = { ...(dst.wrong || {}), ...K.wrong };
      if (K.unfinished != null) dst.unfinished = { ...K.unfinished };
    }
    // vocabulaire des titres (mots proposés en plus de ceux de l'exercice : distracteurs compris)
    for (const [kind, words] of Object.entries(t.words || {})) {
      if (!TABLE_KINDS[kind]) {
        err(pack.id, `tables.words : sorte « ${kind} » inconnue`);
        continue;
      }
      if (!Array.isArray(words) || !words.every(isStr)) {
        err(pack.id, `tables.words.${kind} : une liste de textes`);
        continue;
      }
      (out.words ||= {})[kind] = [...words];
    }
    for (const [role, keys] of Object.entries(t.keys || {})) {
      if (!CHOICE_ROLES.includes(role)) {
        err(pack.id, `tables.keys : rôle « ${role} » inconnu (${CHOICE_ROLES.join(', ')})`);
        continue;
      }
      if (!Array.isArray(keys) || keys.length < 2 || !keys.every(isStr)) {
        err(
          pack.id,
          `tables.keys.${role} : une liste d’au moins deux symboles (toute la famille, jamais la seule bonne réponse)`,
        );
        continue;
      }
      out.keys[role] = [...keys];
    }
  }
  return { tables: out, diagnostics };
}
